import { GLOBAL, APP_SETTINGS, CHAR_LIMITS } from 'config/constants';
import { getReduxStore } from 'src/redux';
import logger from 'core/logger/logger';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { isNullOrUndefined } from 'util';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  signInWithCredential,
  linkWithCredential,
  EmailAuthProvider,
  updatePassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
  getAdditionalUserInfo,
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  indexedDBLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { getGUID, removeEmptyStrings, removeNullOrUndefined } from 'core/utils';
// import { User as FirebaseUser } from 'firebase/app';
import { getKlaviyoInstance } from 'core/libs/klaviyo';
import Recaptcha from 'core/utils/recaptchaClient';
import { hideSessionManager, setMaintenanceAction } from 'src/_common/actions/commonActions';
import { openToastr } from 'src/_common/handlers/services';
import Bugsnag from '@bugsnag/js';

import TrackLogger from 'core/logger/track';
import {
  createUserAction,
  logoutUserAction,
  updateUserAdditionalDetailsAction,
} from '../actions/authenticationActions';
import { User, Provider } from '../models/store';
import {
  signOutUser,
  getAccountDetails,
  updateUserProfile,
  updateEmailSubscriptionStatus,
  signOutFromAllSessions,
} from './api';
import { Metrics } from '../models/api';
import Track from './track';
import { firebase, firebaseConf } from 'config/firebase';
import { initializeApp } from 'firebase/app';

declare const window: any;

const app = initializeApp(firebaseConf);

const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
});

interface FirebaseUserProfile {
  name?: string;
}
class Firestore {
  userType: string;

  updater: Function | null;

  anonID: string;

  authPopupState: string;

  authButtonClicked: string;

  uid: string | undefined;

  authenticated: boolean | null | undefined;

  premium: boolean | undefined;

  num500s: number;

  birthday: string;

  fullName: string;

  isEmailSubscribed: boolean | undefined;

  user: User | null;

  idToken: string | null;

  countryCode: string | null;

  ipApiCount = 0;

  ref: string;

  sessionsExceeded = false;

  sessionManagerHidden = false;

  sessExT: number | undefined;

  private static firestoreInstance: Firestore;

  private constructor() {
    this.authPopupState = '';
    this.authButtonClicked = '';
    this.authenticated = QuillLocalStorage.getInstance().get('authenticated', 'false') === 'true';
    this.user = null;
    this.ref = '';
    this.idToken = null;
    this.userType = 'anon';
    this.anonID = QuillLocalStorage.getInstance().get('anonID', getGUID());
    this.updater = null;
    this.uid = QuillLocalStorage.getInstance().get('quid', '');
    this.premium = QuillLocalStorage.getInstance().get('premium', 'false') === 'true';
    this.num500s = 0;
    this.birthday = '';
    this.fullName = '';
    this.isEmailSubscribed = undefined;
    this.countryCode = null;
  }

  static getInstance = () => {
    if (!Firestore.firestoreInstance) {
      Firestore.firestoreInstance = new Firestore();
    }

    return Firestore.firestoreInstance;
  };

  communicateToOthertabs = (key: string, message: string) => {
    window.localStorage.setItem(key, message);
    window.localStorage.removeItem(key);
  };

  setBirthday = (month: number, day: number, year: number) => {
    const bdayDate = new Date(year, month, day);
    this.birthday = bdayDate.getTime().toString();
  };

  setName = (name: string | undefined) => {
    if (name !== null && name !== undefined) this.fullName = name;
  };

  setEmailSubscription = (isEmailSubscribed: boolean) => {
    this.isEmailSubscribed = isEmailSubscribed;
  };

  sendEmailVerification = async (page?: string) => {
    const loggedInUser = auth.currentUser;
    if (loggedInUser) {
      try {
        await sendEmailVerification(loggedInUser, this.getActionCodeSettings(page));
      } catch (error) {
        Track.verifyEmailFailed();
        throw error;
      }
    }
  };

  // @Test: The values from redux in index.jsx should do this job now.
  /* setUpdater = (updater: Function) => {
    this.updater = updater;
  }; */

  update = () => {
    if (this.updater == null) {
      setTimeout(this.update, 500);
    } else {
      this.updater('auth changed');
    }
  };
  // TODO: removve this

  updateGlobalsCharLimit = () => {
    let newCharacterLimit: unknown;
    if (this.authenticated) {
      if (this.premium) {
        newCharacterLimit = CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_PREMIUM;
      } else newCharacterLimit = CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_MEMBER;
    } else newCharacterLimit = CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_GUEST;

    GLOBAL.CURRENT_CHAR_LIMIT = Number(newCharacterLimit);
  };

  getPlanLabelFromInterval(interval: number) {
    switch (interval) {
      case 1:
        return 'monthly';
      case 6:
        return 'semi annual';
      case 12:
        return 'annual';
    }
  }

  getCountsFromMetrics(details: Metrics) {
    const modeCounts: any = {};
    details.modes_strength?.forEach((mode) => {
      modeCounts[`${mode.mode_type}Count`] = mode.count;
    });
    const data = {
      characterCount: details.character_count,
      copiedTextCount: details.copied_text_count,
      createdOutputCount: details.created_output_count,
      maxedCharsCount: details.maxed_chars_count,
      mode: modeCounts,
      segmentClickedCount: details.segment_click_count,
      selStrengthCount: details.selected_strength_count,
      submittedInputCount: details.submitted_input_count,
      thesaurusedCount: details.thesaurused_count,
    };
    return data;
  }

  signUpTrackAction = (uid: string, fullName: string, email: string) => {
    if (window.growsumo?.data?.partner_key) {
      window.growsumo.data.name = fullName;
      window.growsumo.data.customer_key = uid;
      window.growsumo.data.email = email;
      window.growsumo.createSignup();
      if (uid) updateUserProfile({ uid, affiliatePartnerName: 'PartnerStack' });
      TrackLogger.getInstance().ampSetOnce('affiliatePartnerName', 'PartnerStack');
    }
  };

  /**
   *
   * @param attempts ->
   * @param updateCB ->
   * @param wasForced -> Sent true when update details is called from an event emitter
   */
  updateDetails = (attempts: number, updateCB?: Function, wasForced?: boolean) => {
    if (attempts <= 0 || this.user === null) {
      return;
    }
    this.updateGlobalsCharLimit();
    Track.setUserId(this.user?.email, this.user?.uid);
    const firebaseUser = auth.currentUser;
    if (firebaseUser?.uid) {
      const user: any = {
        uid: firebaseUser.uid,
        email: firebaseUser.providerData[0].email,
        fullName: this.fullName,
        bday: this.birthday,
        ip: '',
        isSubscribedToEmail: this.isEmailSubscribed || false,
        lang: this.detectBrowserLanguage(),
        referrer: this.ref,
      };
      removeNullOrUndefined(user);
      removeEmptyStrings(user);

      getAccountDetails(user) // type of email ie string | undefined is not compatible with string | null
        .then((userDetails) => {
          if (userDetails.created) {
            // Track.authentication('signupSuccess', userDetails.profile.provider as Provider);
            this.signUpTrackAction(userDetails?.uid, userDetails?.full_name, userDetails?.email);
            this.communicateToOthertabs('newUserCreated', JSON.stringify({ userId: userDetails?.uid }));
            if ((userDetails.profile.provider as Provider) === 'email') {
              const loggedInUser = auth.currentUser;
              if (loggedInUser) sendEmailVerification(loggedInUser, this.getActionCodeSettings());
            }
          }

          if (updateCB) updateCB();

          if (userDetails && this.user) {
            this.user.premium = userDetails?.profile.premium;

            if (this.premium !== userDetails?.profile.premium) {
              this.communicateToOthertabs(
                'stateCommunication',
                JSON.stringify({
                  premium: userDetails?.profile.premium,
                })
              );
            }

            if (!userDetails?.profile.premium) {
              if (wasForced) openToastr('Your premium subscription has expired');
              Recaptcha.getInstance().init();
            } else {
              if (wasForced) {
                openToastr('Not able to fetch premium account details, please refresh the page');
                Bugsnag.notify({
                  name: 'AUTH_ERROR',
                  message: 'Premium user given 403. USER_PREMIUM_FORBIDDEN error.',
                });
              }
              Recaptcha.getInstance().destroy();
            }

            this.premium = userDetails?.profile.premium;

            QuillLocalStorage.getInstance().set('authenticated', 'true');
            QuillLocalStorage.getInstance().set('premium', `${this.premium}`);
            QuillLocalStorage.getInstance().set('quid', `${this.user.uid}`);
            QuillLocalStorage.getInstance().set(
              'acceptedPremiumModesTnc',
              `${userDetails?.profile.accepted_premium_modes_tnc}`
            );

            // if (
            //   this.premium &&
            //   QuillLocalStorage.getInstance().get('showMergeModesModal', 'true') === 'true'
            // ) {
            //   getReduxStore()?.dispatch(setMergeModalState(true));
            // }

            // QuillLocalStorage.getInstance().set('showMergeModesModal', 'false');

            const status = this.premium ? 'premium' : 'user';
            Track.setUserStatus(status);
            this.user.syncedWithDb = true;
            this.user.counts = this.getCountsFromMetrics(userDetails?.metrics);
            this.user.demographic = userDetails?.profile.demographic_use_case;
            this.user.educationLevel = userDetails?.profile.demographic_education_level;
            this.user.workType = userDetails?.profile.demographic_work_type;
            this.user.englishSpeaker = userDetails?.profile.demographic_english;
            // this.user.intent = userDetails?.intent;
            this.user.bday = userDetails?.profile.birthday;
            this.user.fullName = userDetails?.full_name;
            this.user.emailSubscriptionStatus = userDetails?.profile.email_subscription_status === 1;
            this.user.paymentMethod = userDetails?.subscription.provider;
            this.user.planLabel = this.getPlanLabelFromInterval(userDetails.subscription.billing_interval);
            this.user.isAuthenticated = true;
            this.user.dollarValue = userDetails?.subscription?.amount
              ? userDetails?.subscription?.amount.toString()
              : undefined;
            this.user.acceptedPremiumModesTnc = userDetails?.profile.accepted_premium_modes_tnc;
            this.user.skippedPersonalization = userDetails?.profile.skipped_personalisation;
            this.user.isSubPaused = userDetails?.subscription.status === 'paused';
            this.user.isSubRenewing = ['active', 'paused'].includes(userDetails?.subscription.status || '');
            this.user.shownNps = userDetails?.profile.nps_shown || false;
            this.user.subscriptionId = userDetails?.subscription.subscription_id;
            this.user.customerId = userDetails?.subscription.customer_id;
            this.user.availedRefund = userDetails?.profile?.availed_refund;
            getReduxStore()?.dispatch(
              updateUserAdditionalDetailsAction({
                syncedWithDb: true,
                isAuthenticated: true,
                counts: this.getCountsFromMetrics(userDetails?.metrics),
                demographic: userDetails?.profile.demographic_use_case,
                englishSpeaker: userDetails?.profile.demographic_english,
                educationLevel: userDetails?.profile.demographic_education_level,
                workType: userDetails?.profile.demographic_work_type,
                // intent: userDetails?.intent,
                bday: userDetails?.profile.birthday,
                fullName: userDetails?.full_name,
                emailSubscriptionStatus: userDetails?.profile.email_subscription_status === 1,
                paymentMethod: userDetails?.subscription.provider,
                planLabel: this.getPlanLabelFromInterval(userDetails.subscription.billing_interval),
                dollarValue: userDetails?.subscription?.amount
                  ? userDetails?.subscription?.amount.toString()
                  : undefined,
                premium: userDetails.profile.premium,
                acceptedPremiumModesTnc: userDetails?.profile.accepted_premium_modes_tnc,
                skippedPersonalization: userDetails?.profile.skipped_personalisation,
                isSubPaused: userDetails?.subscription.status === 'paused',
                isSubRenewing: ['active', 'paused'].includes(userDetails?.subscription.status || ''),
                shownNps: userDetails?.profile.nps_shown || false,
                subscriptionId: userDetails?.subscription.subscription_id,
                customerId: userDetails?.subscription.customer_id,
                availedRefund: userDetails?.profile?.availed_refund,
              })
            );

            this.updateGlobalsCharLimit();
            Track.setUserDataInAmp(this.user);
            getKlaviyoInstance()?.connect(this.user);
            this.update();
          }
        })
        .catch(() => {
          if (this.user) {
            this.user.syncedWithDb = true;
            getReduxStore()?.dispatch(
              updateUserAdditionalDetailsAction({
                syncedWithDb: true,
              })
            );
          }
        });
    } else if (wasForced) openToastr('Please login to continue');
  };

  updateFullName({ uid, name, onSuccess }: { uid: string; name: string; onSuccess?: Function }) {
    updateUserProfile({ uid, fullName: name })
      .then(() => {
        onSuccess && onSuccess();
        getReduxStore()?.dispatch(updateUserAdditionalDetailsAction({ fullName: name }));
        openToastr('Name has been updated');
      })
      .catch((e) => {
        openToastr('Failed to update name');
      });
  }

  toggleUserNotificationSubscription = (uid: string, isSubscribedToEmail: boolean) => {
    updateEmailSubscriptionStatus({
      uid: uid,
      isSubscribedToEmail,
    })
      .then(() =>
        getReduxStore()?.dispatch(
          updateUserAdditionalDetailsAction({
            emailSubscriptionStatus: isSubscribedToEmail,
          })
        )
      )
      .catch(() => {});
  };

  toggleUserPremiumState = (premium: boolean) => {
    getReduxStore()?.dispatch(
      updateUserAdditionalDetailsAction({
        premium,
      })
    );
    QuillLocalStorage.getInstance().set('premium', `${premium}`);
  };

  toggleSubscriptionPauseState = (isSubPaused: boolean) => {
    getReduxStore()?.dispatch(
      updateUserAdditionalDetailsAction({
        isSubPaused,
      })
    );
  };

  updateUserEmailVerificationStatus = (value: boolean) => {
    getReduxStore()?.dispatch(
      updateUserAdditionalDetailsAction({
        emailVerified: value,
      })
    );
  };

  getActionCodeSettings(page?: string) {
    let url = window.location.origin;
    if (page) {
      url += page;
    }
    return {
      url,
    };
  }

  recoverPassword(emailAddress: string) {
    return sendPasswordResetEmail(auth, emailAddress, this.getActionCodeSettings('/login'));
  }

  verifyResetPasswordCode(oobCode: string) {
    return verifyPasswordResetCode(auth, oobCode);
  }

  resetPassword(newPassword: string, oobCode: string) {
    return confirmPasswordReset(auth, oobCode, newPassword);
  }

  async verifyEmail(oobCode: string) {
    const data = await checkActionCode(auth, oobCode);
    const linkEmail = data.data?.email;
    const loggedInEmail = getReduxStore()?.getState()?.user?.email;
    await applyActionCode(auth, oobCode);
    if (loggedInEmail && linkEmail === loggedInEmail) {
      this.updateUserEmailVerificationStatus(true);
    }
  }

  setAuthPopupState(authState: string, btnClicked = '') {
    if (authState !== this.authPopupState) {
      this.authPopupState = authState;
      this.authButtonClicked = btnClicked;
    }
  }

  changePassword(oldPassword: string, newPassword: string, success: Function, error: Function) {
    if (auth.currentUser?.providerData.map((x: { providerId: any }) => x?.providerId).includes('password')) {
      signInWithEmailAndPassword(auth, `${this.user?.email}`, oldPassword)
        .then(() => {
          const user = auth.currentUser;
          if (user) {
            updatePassword(user, newPassword)
              .then(() => {
                if (success) success();
              })
              .catch((err: Function) => {
                if (error) error(err);
              });
          }
        })
        .catch((err: any) => {
          if (error) error(err);
        });
    } else {
      error({
        message: 'User is logged in through either FaceBook or Google.',
      });
    }
  }

  addPassword(pass: string, success: Function, error: Function) {
    if (!auth.currentUser?.providerData.map((x: { providerId: any }) => x?.providerId).includes('password')) {
      const credential = EmailAuthProvider.credential(`${this.user?.email}`, pass);

      const user = auth.currentUser;
      if (user) {
        linkWithCredential(user, credential).then(
          () => success(),
          (e) => error(e)
        );
      }
    } else {
      error({
        message: 'User already has a password.',
      });
    }
  }

  detectBrowserLanguage() {
    return (navigator.languages && navigator.languages[0]) || navigator.language;
  }

  login(
    data: {
      provider: Provider;
      email?: string;
      password?: string;
      newUser?: boolean;
      triggeredBy?: string;
      idToken?: string;
    },
    success: Function,
    error: (reason: { code: string; message: string }) => PromiseLike<never> | void
  ) {
    const providerType = data.provider;
    const handleResult = (result: any) => {
      const additionalUserInfo = getAdditionalUserInfo(result);
      if (additionalUserInfo) {
        if (additionalUserInfo.isNewUser && providerType !== 'email') {
          // for new social authenticated users, set name and subscribe to emails
          this.setEmailSubscription(true);
          if (additionalUserInfo.profile) {
            this.setName((additionalUserInfo.profile as FirebaseUserProfile)?.name);
          }
        }
      }

      this.user = result.user as User;
      const u = result.user;

      if (!u?.email && u && additionalUserInfo?.isNewUser) {
        u.delete().then(() => {
          error({
            code: 'auth/facebook-email-null',
            message: 'Facebook Error',
          });
        });
        return;
      }

      let ref = window.location.search;
      if (ref != null) ref = ref.substring(1);

      const unsubscribe = onAuthStateChanged(auth, (user: unknown) => {
        if (user)
          success({
            created: !!(data.newUser || additionalUserInfo?.isNewUser),
          });
      });
      unsubscribe();

      this.ref = ref;
      const userObj: User = {
        uid: u?.uid,
        fullName: this.fullName || '',
        email: u?.email,
        bday: this.birthday,
        ip: '',
        providerId: providerType,
        isSubscribedToEmail: false,
        lang: this.detectBrowserLanguage(),
        userID: 'N/A',
        referrer: ref,
      };
      if (this.isEmailSubscribed) {
        userObj.isSubscribedToEmail = this.isEmailSubscribed;
      }
      const createNewUserObj = {
        ...userObj,
        userID: [userObj.userID],
        provider: userObj.providerId,
      };
      delete createNewUserObj.providerId;
    };

    if (data.triggeredBy === 'onetap' && providerType === 'google') {
      const cred = GoogleAuthProvider.credential(data.idToken);
      signInWithCredential(auth, cred).then(handleResult).catch(error);
    }

    if (providerType === 'google' && data.triggeredBy !== 'onetap') {
      signInWithPopup(auth, new GoogleAuthProvider(), browserPopupRedirectResolver).then(handleResult).catch(error);
    }
    if (providerType === 'facebook') {
      signInWithPopup(auth, new FacebookAuthProvider(), browserPopupRedirectResolver).then(handleResult).catch(error);
    }
    if (providerType === 'email') {
      let username = data.email || '';
      username = username.trim();
      const password = data.password || '';
      signInWithEmailAndPassword(auth, username, password).then(handleResult).catch(error);
    }
  }

  checkSignInMethod(email: string) {
    return fetchSignInMethodsForEmail(auth, email);
  }

  createUser(
    email: string,
    password: string,
    success: Function,
    error: (result: { code: string; message: string }) => PromiseLike<never> | void
  ) {
    const sanatizedEmail = email.trim();
    createUserWithEmailAndPassword(auth, sanatizedEmail, password)
      .then(() => {
        this.login(
          {
            provider: 'email',
            email: sanatizedEmail,
            password,
            newUser: true,
          },
          success,
          error
        );
      })
      .catch((e: { code: string; message: string }) => {
        error(e);
      });
  }

  nullifyAllAuthParams() {
    this.authenticated = false;
    this.user = null;
    this.premium = false;
    Track.setUserId(null); // @Todo: discuss this
    this.idToken = null;
    this.userType = 'anon';
    QuillLocalStorage.getInstance().delete('useridtoken');
    QuillLocalStorage.getInstance().set('authenticated', 'false');
    QuillLocalStorage.getInstance().set('premium', 'false');
    QuillLocalStorage.getInstance().set('quid', 'false');
    QuillLocalStorage.getInstance().set('acceptedPremiumModesTnc', 'false');
    QuillLocalStorage.getInstance().delete('plagCheckScanHistory');
    QuillLocalStorage.getInstance().delete('hashedEm');
    QuillLocalStorage.getInstance().delete('sessExt');

    getReduxStore()?.dispatch(logoutUserAction());
    this.updateGlobalsCharLimit();
    getKlaviyoInstance()?.disconnect();
    Recaptcha.getInstance().init();
  }

  completeSignOut(cb?: Function) {
    auth.signOut().then(() => {
      this.nullifyAllAuthParams();
      if (cb && typeof cb === 'function') {
        cb();
      }
      Track.authentication('logoutSuccess');
      // QuillEventEmitter.getInstance().emit('LOGOUT_SUCCESS_EVENT', {});
    });
  }

  signOut(cb?: Function) {
    signOutUser()
      .then(() => this.completeSignOut(cb))
      .catch(() => this.completeSignOut(cb));
  }

  async signOutAll() {
    return signOutFromAllSessions()
      .then(() => {
        this.completeSignOut();
        Track.signOutAllSessions({ success: true });
      })
      .catch(() => {
        // should we?
        this.completeSignOut();
        Track.signOutAllSessions({ success: false });
      });
  }

  getUsername() {
    QuillLocalStorage.getInstance().set('anonID', this.anonID);
    return this.user ? this.user.email : `anon-${this.anonID}`;
  }

  getUserID() {
    const user = auth.currentUser;
    if (user) return user.uid;

    return this.uid;
  }

  isPremiumUser() {
    return this.authenticated && this.premium;
  }

  // getPersonalizeData() {
  //   if (
  //     !this.user ||
  //     !this.user.demographic ||
  //     !this.user.englishSpeaker ||
  //     // !this.user.intent
  //     !this.user.educationLevel ||
  //     !this.user.workType
  //   ) {
  //     return null;
  //   }
  //   return {
  //     demographic: this.user.demographic,
  //     englishSpeaker: this.user.englishSpeaker,
  //     // intent: this.user.intent,
  //     educationLevel: this.user.educationLevel,
  //     workType: this.user.workType,
  //   };
  // }

  getAuthToken() {
    return auth.currentUser?.getIdToken(true);
  }

  isLocallyAuthenticated() {
    return QuillLocalStorage.getInstance().get('authenticated', 'false') === 'true';
  }

  checkMaintenanceState() {
    const commonState = getReduxStore()?.getState()?.common;
    const countryCode = commonState?.countryCode;
    return (
      commonState?.maintenance?.isMaintenance ||
      (commonState?.maintenance?.countryWiseMaintenanceCheck?.countryArray?.includes(countryCode) &&
        ((!commonState?.maintenance?.countryWiseMaintenanceCheck?.includePremium &&
          !getReduxStore()?.getState()?.user?.premium) ||
          commonState?.maintenance?.countryWiseMaintenanceCheck?.includePremium))
    );
  }

  setIpBasedData = () => {
    const countryCode = getReduxStore()?.getState()?.common?.countryCode;
    const setData = () => {
      const countryCodesUKEnglish = ['CA', 'GB', 'IN', 'PK', 'EG', 'MY'];
      const countryCodesAUEnglish = ['NZ', 'AU'];
      if (countryCode) {
        // set ipdata in localstorage
        // localStorage.setItem('ipData', JSON.stringify(ipData));
        this.countryCode = countryCode;
        // this.ip = ipData.ip;
        // Emit event to update prices in Premium page
        // QuillEventEmitter.getInstance().emit('IP_DATA_EVENT', ipData || {});
        // getReduxStore()?.dispatch(setIpData(ipData));
        if (this.checkMaintenanceState() && !getReduxStore()?.getState()?.common?.maintenance?.isMaintenance) {
          getReduxStore()?.dispatch(
            setMaintenanceAction({
              ...getReduxStore()?.getState()?.common?.maintenance,
              isMaintenance: true,
            })
          );
        }
        if (this.countryCode?.length === 2) {
          if (countryCodesUKEnglish.includes(this.countryCode)) {
            APP_SETTINGS.REGION = QuillLocalStorage.getInstance().get('REGION') || 'uk';
          } else if (countryCodesAUEnglish.includes(this.countryCode)) {
            APP_SETTINGS.REGION = QuillLocalStorage.getInstance().get('REGION') || 'au';
          }
        }
      }
    };
    setData();
    // try {
    //   if (localStorage) {
    //     const ipData = localStorage.getItem('ipData');
    //     if (ipData) {
    //       // ipData = ;
    //       setData(<IpData>JSON.parse(ipData));
    //       return;
    //     }
    //   }
    // } catch (err) {
    //   logger('err in getting localstorage data ', err);
    // }
    // getIpData()
    //   .then(setData)
    //   .catch((error) => {
    //     this.ipApiCount += 1;
    //     let errorData = 'No error data';
    //     if (typeof error === 'object') {
    //       errorData = JSON.stringify(error);
    //     } else if (typeof error === 'string') {
    //       errorData = error;
    //     }
    //     Bugsnag.notify({
    //       name: "Couldn't get IP data",
    //       message: `errorData: ${errorData} `,
    //     });
    //     // QuillEventEmitter.getInstance().emit('IP_DATA_EVENT', {});
    //     if (this.ipApiCount < 2) {
    //       setTimeout(this.setIpBasedData, 10000);
    //     }
    //   });
  };

  registerFirebaseListeners() {
    logger('Firebase Initialized', {});
    // @TODO: check if user set event is working fine
    Track.setUserStatus('guest');
    onAuthStateChanged(auth, (user: any) => (user ? this.handleLoginUser(user) : this.nullifyAllAuthParams()));

    this.setIpBasedData();
    this.setMaintenanceModeListener();
    window.addEventListener('storage', this.handleStorageMessageReceived);
  }

  async setMaintenanceModeListener() {
    const { getDatabase, onValue, ref } = await firebase.database;

    onValue(
      ref(getDatabase(), 'healthCheck'),
      (snapshot: {
        val: () => {
          (): any;
          new (): any;
          isMaintenance: any;
          countryWiseMaintenanceCheck: {
            (): any;
            new (): any;
            countryArray: string[];
            includePremium: boolean;
          };
          globalMaintenance: {
            (): any;
            new (): any;
            enabled: boolean;
            message: string;
          };
          maintenanceNotification: {
            (): any;
            new (): any;
            alertMessage: any;
            alertTitle: any;
            barMessage: any;
          };
        };
      }) => {
        if (snapshot.val()) {
          getReduxStore()?.dispatch(
            setMaintenanceAction({
              isMaintenance: snapshot.val().isMaintenance,
              globalMaintenance: snapshot.val().globalMaintenance,
              barMessage: snapshot.val().maintenanceNotification.barMessage,
              alertMessage: snapshot.val().maintenanceNotification.alertMessage,
              alertTitle: snapshot.val().maintenanceNotification.alertTitle,
              countryWiseMaintenanceCheck: snapshot.val().countryWiseMaintenanceCheck,
            })
          );
          if (this.checkMaintenanceState() && !getReduxStore()?.getState()?.common?.maintenance?.isMaintenance) {
            getReduxStore()?.dispatch(
              setMaintenanceAction({
                ...getReduxStore()?.getState()?.common?.maintenance,
                isMaintenance: true,
              })
            );
          }
        }
      }
    );
  }

  setLocalAuthenticatedStatus() {
    getReduxStore()?.dispatch(
      createUserAction({
        isAuthenticated: QuillLocalStorage.getInstance().get('authenticated', 'false') === 'true',
      })
    );
  }

  setSessionFlags({ exceeded, hideManager }: { exceeded: boolean; hideManager: boolean }) {
    if (exceeded !== this.sessionsExceeded) {
      getReduxStore()?.dispatch(updateUserAdditionalDetailsAction({ sessionsExceeded: exceeded }));
      this.sessionsExceeded = exceeded;
    }
    if (hideManager !== this.sessionManagerHidden) {
      getReduxStore()?.dispatch(hideSessionManager(hideManager));
      this.sessionManagerHidden = hideManager;
    }
  }

  setSessionExpiryData(time: string) {
    this.sessExT = new Date(time).getTime();
    QuillLocalStorage.getInstance().set('sessExT', this.sessExT.toString());
  }

  isSessionValid() {
    if (!this.sessExT) {
      const data = QuillLocalStorage.getInstance().get('sessExT');
      if (!data) {
        // If no date exists in local storage, we set one temporarily to assign value in memory. This value will quickly be updated by the setSessionExpiryDate function that populates this information with the right date
        const exp = new Date();
        exp.setDate(exp.getDate() + 1);
        this.sessExT = exp.getTime();
        QuillLocalStorage.getInstance().set('sessExT', this.sessExT.toString());
        return true;
      }
      if (data && !isNaN(Number(data))) this.sessExT = Number(data);
      else return false;
    }
    const diff = this.sessExT - new Date().getTime();
    return diff > 60000; // 10 * 60 * 1000
  }

  forceUpdateSessionExpiry() {
    const exp = new Date();
    exp.setDate(exp.getDate() + 1);
    this.sessExT = exp.getTime();
    QuillLocalStorage.getInstance().set('sessExT', this.sessExT.toString());
  }

  handleStorageMessageReceived = (ev: StorageEvent) => {
    let message = null;
    if (ev.key === 'stateCommunication' || ev.key === 'newUserCreated') {
      try {
        message = JSON.parse(ev.newValue ? ev.newValue : '');
      } catch (e) {
        message = {};
        logger('ERR_PARSING_MESSAGE', e);
      }
      if (isNullOrUndefined(message)) return;
      if (message.premium !== this.premium || message.userId === this.user?.uid) {
        this.updateDetails(3);
      }
    }
  };

  updateFirestoreUser(obj: User) {
    this.user = { ...this.user, ...obj };
    this.updateDetails(5);
  }

  handleLoginUser(user: FirebaseUser) {
    auth.currentUser
      ?.getIdToken(true)
      .then((idToken: string | null) => {
        this.authenticated = true;
        this.user = <User>user;
        this.idToken = idToken;
        this.userType = 'account';

        const userData = {
          providerId: user.providerData?.[0]?.providerId,
          providerData: user.providerData,
          displayName: user.displayName,
          email: user.email ? user.email : 'anon',
          emailVerified: user.emailVerified,
          creationTime: user.metadata.creationTime,
          lastSignInTime: user.metadata.lastSignInTime,
          phoneNumber: user.phoneNumber,
          photoUrl: user.photoURL,
          uid: user.uid,
          userType: 'account',
          bday: '',
          isLoading: false,
          lang: this.detectBrowserLanguage(),
          isEmailSubscribed: false,
          referrer: '',
          isAuthenticated: true,
          syncedWithDb: false,
        };
        getReduxStore()?.dispatch(createUserAction(userData));

        this.updateDetails(5);
      })
      .catch(() => this.nullifyAllAuthParams());
  }
}
export default Firestore;
export { Firestore };
